<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Журнал записей и&nbsp;их данные</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="zhurnal-zapisej"
            class="typography__headline2"
          >
            Журнал записей
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Журнал записей&raquo;.</p>

            <p>
              Сверху справа можно добавить новую запись или переключить вид с&nbsp;календаря сотрудников на&nbsp;список.
            </p>

            <p>
              В&nbsp;контроллах сверху слева можно переключить день вперед или назад, а&nbsp;по&nbsp;нажатию
              &laquo;Сегодня&raquo; быстро переместиться к&nbsp;текущему.
            </p>

            <p>
              На&nbsp;календаре есть карточки записей. Оранжевые&nbsp;&mdash; индивидуальные записи, белые&nbsp;&mdash;
              групповые, а&nbsp;серые&nbsp;&mdash; карточки перерыва. При нажатии на&nbsp;них, можно посмотреть страницу
              записи и&nbsp;перейти к&nbsp;её&nbsp;редактированию.
            </p>

            <p>Синяя полоска через весь журнал&nbsp;&mdash; индикатор текущего вашего времени.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/zhurnal-zapisej-i-ikh-dannye-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>При переключении вида на&nbsp;список, можно также увидеть всю важную информацию о&nbsp;записях.</p>

            <p>Цветные индикаторы слева показывают статус записи.</p>

            <p>Отменённые записи будет видно только в&nbsp;списке, в&nbsp;календаре они не&nbsp;выводятся.</p>

            <p>
              При нажатии на&nbsp;нужную запись можно посмотреть её&nbsp;страницу и&nbsp;перейти к&nbsp;редактированию.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/zhurnal-zapisej-i-ikh-dannye-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>Нажав сверху на&nbsp;иконку со&nbsp;стрелочкой откроется календарь и&nbsp;фильтры.</p>

            <p>
              В&nbsp;календаре выделены текущий и&nbsp;выбранный дни. По&nbsp;нажатию на&nbsp;нужный, можно быстро
              переключиться на&nbsp;него.
            </p>

            <p>
              В&nbsp;фильтрах можно настроить показ записей только по&nbsp;определенным адресам, сотрудникам или
              услугам.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/zhurnal-zapisej-i-ikh-dannye-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="stranica-zapisi"
            class="typography__headline2"
          >
            Страница записи
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              На&nbsp;открывшейся странице записи сверху можно увидеть её&nbsp;тип, а&nbsp;под ним находится быстрый
              доступ к&nbsp;редактированию или удалению.
            </p>

            <p>Ниже находится вся нужная информация о&nbsp;записи.</p>

            <p>
              Статус записи и&nbsp;статусы посещения и&nbsp;оплаты клиента проставляются отдельно
              и&nbsp;по&nbsp;желанию.
            </p>

            <p>Отсюда можно открыть страницы сотрудников, клиента и&nbsp;услуг записи, нажав на&nbsp;них.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/zhurnal-zapisej-i-ikh-dannye-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <FaqArticlesImageModalButton
            image-source="/images/faq/zhurnal-zapisej-i-ikh-dannye-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С добавлением записей их просмотром разобрались. Самое время перейти к изучению
            <BaseLink to="/faq/categories/zayavki-na-uslugi">заявок на услуги</BaseLink>.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Онлайн-&#65279;запись",
    url: "/faq/categories/onlayn-zapis",
  },
  {
    name: "Журнал записей и&nbsp;их данные",
  },
];

const menu = [
  {
    name: "Журнал&nbsp;записей",
    url: "zhurnal-zapisej",
  },
  {
    name: "Страница&nbsp;записи",
    url: "stranica-zapisi",
  },
];

const relatedArticles = [
  {
    name: "Настройки записей",
    slug: "nastroyki-zapisey",
  },
  {
    name: "Категории услуг",
    slug: "kategorii-uslug",
  },
  {
    name: "Добавление и&nbsp;редактирование услуги",
    slug: "dobavleniye-i-redaktirovaniye-uslugi",
  },
  {
    name: "Список услуг и&nbsp;их данные",
    slug: "spisok-uslug-i-ikh-dannye",
  },
];

useSeoMeta({
  title: "Журнал записей и их данные",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
